/* eslint-disable no-bitwise */
import { invariant } from '../../util/invariant';
import type { StructuredLogger } from '@mimir/primitive/structuredLogger';

import type { IndexTableSegment } from './indexTableSegment';

import {
  INDEX_ENTRY_FLAGS_B_FRAME,
  INDEX_ENTRY_FLAGS_P_FRAME,
  INDEX_ENTRY_FLAG_BACKWARD_PREDICTION_BIT,
  INDEX_ENTRY_FLAG_FORWARD_PREDICTION_BIT,
  INDEX_ENTRY_FLAG_RANDOM_ACCESS_BIT,
  INDEX_ENTRY_FLAG_SEQUENCE_HEADER_BIT,
  parseIndexTableSegment
} from './indexTableSegment';

import type { ParsedKLV } from './klv';
import type { FrameType, MimirIndexEntry, MimirIndexFile, MimirIndexHeader } from './mimirIndexFile';
import { MxfDictionary, SMPTE_UL_INDEX_TABLE_SEGMENT, SMPTE_UL_KLV_FILLS, SMPTE_UL_PARTITION_PACKS } from './mxfDictionary';
import type { PartitionPack } from './partitionPack';
import { parsePartitionPack } from './partitionPack';
import { mxfOperationalPatternName } from './mxfOperationPattern';
import type { StructuralMetadataReserializer } from './structuralMetadataReserializer';
import { makeStructuralMetadataReserializer } from './structuralMetadataReserializer';
import { safeArrayAccess } from '@mimir/common/util/safeArrayAccess';

export interface IndexHandler {
  // Whenever the index file generator determines that the file is a fixed
  // frame size MXF it can immediately provide the entire MimirIndexFile as
  // an object.
  onIndex(index: MimirIndexFile): void;

  // If the MXF file is determined to have variable frame size the IndexHandler may return
  // a IndexEntryHandler in order to dynamically get called whenever a new IndexEntry is
  // encountered. If this method return null then the MimirIndexFileGenerator will build up
  // the variable frame size index internally and only call onIndex when the last index has
  // been read.
  createIndexEntryHandler(header: MimirIndexHeader): IndexEntryHandler | null;
}

export interface IndexEntryHandler {
  // onEntry will be called for each index entry in each index table segment as they are
  // encountered
  onEntry(entry: MimirIndexEntry): void;

  // close will be called when the mimir index file generator is flushed.
  close(): void;
}

export class MimirIndexFileGenerator {
  private indexEntryBuilder : IndexEntryBuilder;
  private dictionary: MxfDictionary = new MxfDictionary();
  private currentOffset : number = 0;
  private currentPartition: PartitionPack | undefined;
  private headerDataStart : number = 0;
  private headerDataEnd : number = 0;
  private structuralMetadataParser : StructuralMetadataReserializer | null = null;

  // This is the KLV key for the first KLV essence in a frame.
  // For instance in some files the audio packets will come before
  // the video packets while in other files it is the system
  // metadata packets that come first. Undefined means we have not
  // encountered any essence yet.
  private firstEssenceKlvType : string | undefined;
  private firstFrameByteOffset : number | undefined;
  private state: IndexGeneratorState;

  constructor(
    private structuredLogger: StructuredLogger,
    handler: IndexHandler
  ) {
    this.indexEntryBuilder = new IndexEntryBuilder(structuredLogger);
    this.state = { type: 'uninitialized', handler };
  }

  public parseKlv(klv: ParsedKLV) {
    this.parseKlvInternal(klv);
    this.currentOffset += klv.totalSize;
  }

  public flush() {
    if (this.state.type === 'variable') {
      this.state.handler.onIndex({
        header: this.state.header,
        index: {
          type: 'variable_frame_size',
          entries: this.state.entries,
        }
      });
    }
    if (this.state.type === 'growing') {
      this.state.entryHandler.close();
    }
    this.state = { type: 'done' };
  }

  // End of public API

  private parseKlvInternal(klv: ParsedKLV) {
    if (this.state.type === 'done') {
      return;
    }

    if (SMPTE_UL_PARTITION_PACKS.has(klv.key)) {
      this.indexEntryBuilder.endFrame(this.currentOffset);
      this.currentPartition = parsePartitionPack(klv);
      const operationalPatternName = mxfOperationalPatternName(this.currentPartition.operationalPattern);
      if (operationalPatternName !== 'OP1a') {
        throw new Error(`Unsupported operational pattern: ${operationalPatternName ?? this.currentPartition.operationalPattern}`);
      }


      this.headerDataStart = this.currentOffset + klv.totalSize;
      this.headerDataEnd = this.headerDataStart + this.currentPartition.headerByteCount;
      return;
    }

    if (SMPTE_UL_KLV_FILLS.has(klv.key)) {
      if (this.headerDataStart === this.currentOffset) {
        // This indicates that this is the KLVFill following the PartitionPack. This is not
        // considered part of the header data.
        this.headerDataStart += klv.totalSize;
        this.headerDataEnd += klv.totalSize;
      }
      return;
    }

    if (this.currentPartition == undefined) {
      return;
    }

    // We parse the structural metadata just to make sure that our MXF parse is actually able to
    // understand this MXF file. This way we skip creating index files for unsupported files so
    // that we don't get errors later down the line during conform for instance.
    if (this.currentOffset < this.headerDataEnd) {
      if (this.structuralMetadataParser === null) {
        this.structuralMetadataParser = makeStructuralMetadataReserializer(this.structuredLogger);
      }
      this.structuralMetadataParser.parseKlv(klv);
      // We only care about IndexTableSegments and Actual essence KLVs
      return;
    }

    if (this.structuralMetadataParser !== null) {
      this.structuredLogger.log({
        level: 'debug',
        message: 'Parsed MXF metadata',
        preface: this.structuralMetadataParser.finish()
      });

      this.structuralMetadataParser = null;
    }

    if (klv.key === SMPTE_UL_INDEX_TABLE_SEGMENT) {
      const indexTableSegment = parseIndexTableSegment(this.structuredLogger, klv.value, this.dictionary);
      makeSureIndexTableIsSupported(indexTableSegment);
      this.onIndexTableSegment(indexTableSegment, klv);
      return;
    }

    // Since we are not in KLVFill, not in the Header and not in the IndexTable we must be in
    // the essence data.

    if (this.currentPartition.partitionType === 'footer') {
      // There is no essence in the footer. This check prevents us from potentially reading the
      // RandomIndexPack as essence.
      return;
    }

    if (this.firstEssenceKlvType === undefined) {
      this.firstEssenceKlvType = klv.key;
      this.firstFrameByteOffset = this.currentOffset;
      this.completeFixedFrameIndex();
    }

    if (klv.key === this.firstEssenceKlvType) {
      this.indexEntryBuilder.startFrame(this.currentOffset);
      this.flushAndLog();
    }
  }

  private completeFixedFrameIndex() {
    if (this.state.type !== 'fixed') {
      return;
    }
    if (this.firstFrameByteOffset === undefined) {
      return;
    }

    const firstFrameSize = this.indexEntryBuilder.getFirstFrameSize() ;
    if (firstFrameSize === null) {
      this.structuredLogger.log({ level: 'info', message: 'Waiting for actual frame size to be determined' });
      return;
    }

    if (firstFrameSize !== this.state.fixedFrameSize) {
      this.structuredLogger.log({ level: 'warn', message: 'Incorrect EditUnitLength reported in index table', reportedEditUnitLength: this.state.fixedFrameSize, actualEditUnitLength: firstFrameSize });
    }

    this.state.handler.onIndex({
      header: this.state.header,
      index: {
        type: 'fixed_frame_size',
        fixedFrameSize: firstFrameSize,
        firstFrameByteOffset: this.firstFrameByteOffset,
      }
    });

    this.state = { type: 'done' };
  }

  private onIndexTableSegment(indexTableSegment: IndexTableSegment, klv: ParsedKLV) {
    if (this.state.type === 'uninitialized') {
      const handler = this.state.handler;

      const header : MimirIndexHeader = {
        firstIndexTableOffset: this.currentOffset,
        firstIndexTableSize: klv.totalSize,
      };

      if (indexTableSegment.editUnitByteCount !== 0) {
        this.state = {
          type: 'fixed',
          header,
          fixedFrameSize: indexTableSegment.editUnitByteCount,
          handler,
        };
      } else {
        const entryHandler = handler.createIndexEntryHandler(header);
        if (entryHandler !== null) {
          this.state = { type: 'growing', entryHandler };
        } else {
          this.state = {
            type: 'variable',
            header,
            entries: [],
            handler,
          };
        }
      }
    }

    this.completeFixedFrameIndex();
    this.indexEntryBuilder.pushIndexTableSegment(indexTableSegment);
    this.flushAndLog();
  }

  private flushAndLog() {
    this.completeFixedFrameIndex();

    if ((this.state.type !== 'variable') && (this.state.type !== 'growing')) {
      // Only the variable and growing state needs to flush out index entries
      return;
    }

    const entries = this.indexEntryBuilder.buildIndexEntries();

    if (entries.length > 1) {
      this.structuredLogger.log({ level: 'info', message: `Flushed out ${entries.length} MimirIndexFile entries` });
    }

    if (this.state.type === 'variable') {
      this.state.entries.push(...entries);
    } else if (this.state.type === 'growing') {
      for (const entry of entries) {
        this.state.entryHandler.onEntry(entry);
      }
    }
  }
}

// The variable frame size Mimir index file entries contains both information from
// the MXF index table and the actual offset of the frame in the file. Because of
// this we can not flush out an index entry until both the frame and the index entry
// for the frame is encountered. The IndexEntryBuilder class is responsible for
// keeping track of pending frames and pending index table entries that we've yet
// to flush because we are missing the other parts.
class IndexEntryBuilder {
  private pendingIndexTableSegments: IndexTableSegment[] = [];
  private currentFrame: IncompleteFrame | null = null;
  private completeFrames: Frame[] = [];
  private entryIndex = 0;

  constructor(private structuredLogger: StructuredLogger) { }

  public pushIndexTableSegment(indexTableSegment: IndexTableSegment) {
    this.pendingIndexTableSegments.push(indexTableSegment);
  }

  public startFrame(offset: number) {
    if (this.currentFrame !== null) {
      this.endFrame(offset);
    }
    // The number of flushed index entries (entryIndex) plus the
    // number of pending encountered frames gives the total frame
    // number.
    const frameNumber = this.entryIndex + this.completeFrames.length;
    this.currentFrame = { frameNumber, startOffset: offset };
  }

  public endFrame(offset: number) {
    if (this.currentFrame !== null) {
      const byteSize = offset - this.currentFrame.startOffset;
      this.completeFrames.push({ ...this.currentFrame, byteSize });
      this.currentFrame = null;
    }
  }

  public getFirstFrameSize(): number | null {
    if (this.completeFrames.length < 2) {
      return null;
    }
    return this.completeFrames[1]!.startOffset - this.completeFrames[0]!.startOffset;
  }

  public buildIndexEntries(): MimirIndexEntry[] {
    const entries : MimirIndexEntry[] = [];
    while (this.pendingIndexTableSegments.length > 0) {
      const nextIndexTableSegment = safeArrayAccess(this.pendingIndexTableSegments, 0);


      while (true) {
        const indexTableOffset = this.entryIndex - nextIndexTableSegment.indexStartPosition;

        if (indexTableOffset < 0) {
          this.structuredLogger.log({ level: 'info', message: `Ignoring repeated index table ${nextIndexTableSegment.instanceUid}` });
          break;
        }

        if (indexTableOffset >= nextIndexTableSegment.indexDuration) {
          // We used all the entries for the index table. Let check
          // the next one
          break;
        }

        if (this.completeFrames.length === 0) {
          // We don't have encountered this frame yet so we don't know
          // its duration. Because of the we exit from this method now
          return entries;
        }


        const sourceEntry = safeArrayAccess(nextIndexTableSegment.indexEntries, indexTableOffset);
        const [encounteredFrame] = this.completeFrames.splice(0, 1);
        invariant(encounteredFrame !== undefined, 'Encountered index table without encountering frame');
        const entry : MimirIndexEntry = {
          frameByteOffset: encounteredFrame.startOffset,
          frameSize: encounteredFrame.byteSize,
          frameType: flagsToFrameType(sourceEntry.flags),
          randomAccess: flagsToRandomAccess(sourceEntry.flags),
          temporalOffset: sourceEntry.temporalOffset,
          keyFrameOffset: sourceEntry.keyFrameOffset,
          sliceOffsets: sourceEntry.sliceOffsets,
        };
        entries.push(entry);
        this.entryIndex += 1;
      }

      // The loop above is only broken in the case that we are done with the current
      // pending IndexTableSegment
      this.pendingIndexTableSegments.splice(0, 1);
    }
    return entries;
  }
}

interface Uninitialized {
  type: 'uninitialized';
  handler: IndexHandler,
}

// Once a fixed frame size index table is encountered we enter this state.
// The index table will be flushed to the indexHandler.onIndex when the
// first frame is encountered. At that point the index generator will go
// to the done state.
interface Fixed {
  type: 'fixed';
  header: MimirIndexHeader,
  fixedFrameSize: number,
  handler: IndexHandler,
}

// If a variable frame size index table is encountered and the index handler
// returns a IndexEntryHandler we enter growing mode where the entryHandler
// will be called for each index entry countered in the file.
interface Growing {
  type: 'growing',
  entryHandler: IndexEntryHandler,
}

// If a variable frame size index table is encountered and the index handler
// does NOT return a IndexEntryHandler we start accumulating index entries
// and then flush the entire index file when all index entries has been read.
interface Variable {
  type: 'variable',
  header: MimirIndexHeader,
  entries: MimirIndexEntry[],
  handler: IndexHandler,
}

interface Done {
  type: 'done';
}


type IndexGeneratorState =
  Uninitialized |
  Fixed |
  Growing |
  Variable |
  Done;

interface IncompleteFrame {
  frameNumber: number,
  startOffset: number,
}
type Frame = IncompleteFrame & {
  byteSize: number,
};


function flagsToRandomAccess(flags: number): boolean {
  return (flags & INDEX_ENTRY_FLAG_RANDOM_ACCESS_BIT) != 0;
}

function flagsToFrameType(flags: number): FrameType {
  if ((flags & INDEX_ENTRY_FLAG_BACKWARD_PREDICTION_BIT) != 0) {
    invariant((flags & INDEX_ENTRY_FLAGS_B_FRAME) === INDEX_ENTRY_FLAGS_B_FRAME, 'Expected bit 0 and 1 to be set for B-frames');
    if ((flags & INDEX_ENTRY_FLAG_FORWARD_PREDICTION_BIT) != 0) {
      return 'b';
    } else {
      return 'B';
    }
  }
  if ((flags & INDEX_ENTRY_FLAG_FORWARD_PREDICTION_BIT) != 0) {
    invariant((flags & INDEX_ENTRY_FLAGS_P_FRAME) === INDEX_ENTRY_FLAGS_P_FRAME, 'Expected bit 1 to be set for O-frames');
    return 'p';
  }

  invariant(
    (flags & INDEX_ENTRY_FLAG_SEQUENCE_HEADER_BIT) != 0,
    'Expected I-frame to have sequence header bit set',
  );

  return 'I';
}

function makeSureIndexTableIsSupported(indexTableSegment: IndexTableSegment) {
  // PosTableCount is way to describe how much of an audio packet overlaps
  // with the given edit unit. This is only necessary for audio codec with
  // fixed size packets (AAC, MP3, MP2 etc.). So far I've only encountered
  // PCM, RIFF and AES3 which are always exactly the size of the edit unit
  // so PosTable is not in use.
  if (indexTableSegment.posTableCount) {
    throw new Error('PosTableIndex not yet supported');
  }

  for (const entry of indexTableSegment.deltaEntries) {
    if (entry.posTableIndex > 0) {
      throw new Error('PosTableIndex not yet supported');
    }
  }
}


